import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { money, moneyClass, pct, dateShort, cycleLabel, rateLabel } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import AddDealDialog from "@/components/AddDealDialog";
import OnboardingTab from "@/components/OnboardingTab";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Snowflake, CheckCircle, ArrowCounterClockwise, CurrencyDollar } from "@phosphor-icons/react";

export default function RepDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [checklist, setChecklist] = useState([]);

  const load = useCallback(() => {
    api.get(`/reps/${id}`).then((r) => setData(r.data));
    api.get(`/reps/${id}/checklist`).then((r) => setChecklist(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    try { await api.post(`/reps/${id}/status`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const collect = async (dealId, source) => {
    try { await api.post(`/deals/${dealId}/collect`, { payment_source: source }); toast.success("Marked collected"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const refund = async (dealId) => {
    try { await api.post(`/deals/${dealId}/refund`, {}); toast.success("Refund recorded — run reconciliation to claw back"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const offboard = async () => {
    if (!window.confirm("Offboard this rep? Residual accrual freezes immediately; ledger history is retained and a final statement is generated.")) return;
    try { await api.post(`/reps/${id}/offboard`); toast.success("Rep offboarded — residuals frozen, final statement generated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="font-mono text-sm text-muted-foreground">Loading…</div>;
  const { rep, commission_profile: prof, deals, events, statements } = data;

  return (
    <div>
      <button data-testid="back-btn" onClick={() => navigate("/reps")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={15} /> Reps
      </button>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-black tracking-tight">{rep.name}</h1>
            <StatusBadge status={rep.status} />
          </div>
          <p className="mt-1 font-mono text-sm text-muted-foreground">{rep.email} · {rep.phone || "no phone"} · {rep.home_base || "—"}</p>
        </div>
        <div className="flex gap-2">
          <AddDealDialog repId={id} onCreated={load} />
          {rep.status !== "inactive" && (
            <Button data-testid="offboard-btn" variant="outline" className="text-destructive hover:text-destructive" onClick={offboard}>Offboard</Button>
          )}
          <Select value={rep.status} onValueChange={setStatus}>
            <SelectTrigger data-testid="rep-status-select" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="frozen">Frozen</SelectItem>
              <SelectItem value="inactive">Inactive (offboard)</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {rep.contract_status !== "signed" && (
        <div data-testid="activation-banner" className="mb-6 flex items-center gap-3 rounded-sm border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-4">
          <span className="text-sm text-[hsl(var(--warning))]">
            {rep.contract_status === "needs_resign"
              ? "Terms changed — contract needs re-signing. Regenerate & re-sign in Onboarding & Contract."
              : "Not activated — commission is frozen until a contract is generated and signed. Go to Onboarding & Contract."}
          </span>
        </div>
      )}

      <div className="mb-6"><OnboardingChecklist steps={checklist} /></div>

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-sm border border-border bg-card p-4"><div className="overline">Rate Model</div><div className="mt-1 font-mono text-sm font-medium">{rateLabel(prof)}</div></div>
        <div className="rounded-sm border border-border bg-card p-4"><div className="overline">Recurring</div><div className="mt-1 font-mono text-sm">{prof?.recurring_mode === "residual" ? "Residual" : "One-time"}</div></div>
        <div className="rounded-sm border border-border bg-card p-4"><div className="overline">Engagement-gated</div><div className="mt-1 font-mono text-sm">{(prof?.residual_gated_on_active_engagement ?? prof?.residual_gated_on_active_employment) ? "Yes" : "No"}</div></div>
        <div className="rounded-sm border border-border bg-card p-4"><div className="overline">Clawback / Inactivity</div><div className="mt-1 font-mono text-sm">{prof?.clawback_window_days}d / {prof?.inactivity_freeze_days}d</div></div>
      </div>

      <Tabs defaultValue="events">
        <TabsList>
          <TabsTrigger value="events" data-testid="tab-events">Commission Events</TabsTrigger>
          <TabsTrigger value="deals" data-testid="tab-deals">Deals</TabsTrigger>
          <TabsTrigger value="statements" data-testid="tab-statements">Statements</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding">Onboarding &amp; Contract</TabsTrigger>
        </TabsList>

        <TabsContent value="onboarding" className="mt-4">
          <OnboardingTab rep={rep} onReload={load} />
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <TableWrap cols={["Cycle", "Customer", "Product", "Sale", "Rate", "Commission", "Kind", "Status"]}>
            {events.length === 0 && <EmptyRow span={8} text="No commission events yet. Record a deal, then run reconciliation." />}
            {events.map((e) => (
              <tr key={e.id} className="border-b border-border hover:bg-accent transition-colors duration-200">
                <td className="px-4 py-2.5 font-mono">{cycleLabel(e.cycle)}</td>
                <td className="px-4 py-2.5">{e.customer}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{e.product}</td>
                <td className="px-4 py-2.5 text-right font-mono">{money(e.sale_amount)}</td>
                <td className="px-4 py-2.5 text-right font-mono">{e.rate != null ? pct(e.rate * 100) : "flat"}</td>
                <td className={`px-4 py-2.5 text-right font-mono font-medium ${moneyClass(e.commission_amount)}`}>{money(e.commission_amount)}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{e.kind}</td>
                <td className="px-4 py-2.5"><StatusBadge status={e.status} /></td>
              </tr>
            ))}
          </TableWrap>
        </TabsContent>

        <TabsContent value="deals" className="mt-4">
          <TableWrap cols={["Won", "Customer", "Product", "Amount", "Recurring", "Collected", "Actions"]}>
            {deals.length === 0 && <EmptyRow span={7} text="No deals recorded." />}
            {deals.map((d) => (
              <tr key={d.id} className="border-b border-border hover:bg-accent transition-colors duration-200">
                <td className="px-4 py-2.5 font-mono">{dateShort(d.won_date)}</td>
                <td className="px-4 py-2.5">{d.customer}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{d.product}</td>
                <td className="px-4 py-2.5 text-right font-mono">{money(d.sale_amount)}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{d.is_recurring ? "monthly" : "one-time"}</td>
                <td className="px-4 py-2.5">
                  {d.refunded ? <StatusBadge status="reversed" /> : d.payment_confirmed
                    ? <span className="font-mono text-xs text-[hsl(var(--success))]">{d.payment_source}</span>
                    : <span className="font-mono text-xs text-[hsl(var(--warning))]">pending</span>}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex justify-end gap-1">
                    {!d.payment_confirmed && !d.refunded && (
                      <Button data-testid={`collect-${d.id}`} size="sm" variant="ghost" className="h-7 gap-1 px-2 text-[hsl(var(--success))]" onClick={() => collect(d.id, "stripe")}>
                        <CheckCircle size={14} /> Collect
                      </Button>
                    )}
                    {d.payment_confirmed && !d.refunded && (
                      <Button data-testid={`refund-${d.id}`} size="sm" variant="ghost" className="h-7 gap-1 px-2 text-destructive" onClick={() => refund(d.id)}>
                        <ArrowCounterClockwise size={14} /> Refund
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </TableWrap>
        </TabsContent>

        <TabsContent value="statements" className="mt-4">
          <TableWrap cols={["Cycle", "Gross", "Adjustments", "Paid", "Net Payable", "Status"]}>
            {statements.length === 0 && <EmptyRow span={6} text="No statements yet. Generate a Payout Run." />}
            {statements.map((s) => (
              <tr key={s.id} className="border-b border-border hover:bg-accent transition-colors duration-200">
                <td className="px-4 py-2.5 font-mono">{cycleLabel(s.cycle)}</td>
                <td className="px-4 py-2.5 text-right font-mono">{money(s.gross_earned)}</td>
                <td className={`px-4 py-2.5 text-right font-mono ${moneyClass(s.adjustments)}`}>{money(s.adjustments)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-muted-foreground">{money(s.already_paid)}</td>
                <td className="px-4 py-2.5 text-right font-mono font-medium">{money(s.net_payable)}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={s.status} />
                    <button data-testid={`dl-stmt-${s.id}`} onClick={() => window.open(`${API}/statements/${s.id}/pdf`, "_blank")} className="text-xs text-primary hover:underline">PDF</button>
                  </div>
                </td>
              </tr>
            ))}
          </TableWrap>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TableWrap({ cols, children }) {
  return (
    <div className="overflow-hidden rounded-sm border border-border">
      <table className="w-full text-sm">
        <thead className="bg-card"><tr className="border-b border-border">
          {cols.map((c, i) => <th key={c} className={`px-4 py-3 overline ${i > 1 && i !== cols.length - 0 ? "text-right" : "text-left"}`}>{c}</th>)}
        </tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
function EmptyRow({ span, text }) {
  return <tr><td colSpan={span} className="px-4 py-12 text-center text-sm text-muted-foreground">{text}</td></tr>;
}
